import { useState } from 'react'
import { t } from '../i18n/index.js'
import type { OverlayTask } from './shell.js'
import { TaskCard } from './TaskCard.js'

export interface Group {
  key: string
  propertyName: string
  address: string
  tasks: OverlayTask[]
}

/**
 * Tasks grouped by address.
 *
 * Nobody works through their day chronologically across town, they drive to the
 * Lindenstraße and do everything there. Grouping by property matches that, and
 * it means one navigation link per address instead of one per task.
 */
export function groupByProperty(tasks: OverlayTask[]): Group[] {
  const groups = new Map<string, Group>()
  for (const task of tasks) {
    const key = task.siteId ?? task.siteName
    const existing = groups.get(key)
    if (existing) {
      existing.tasks.push(task)
      continue
    }
    groups.set(key, {
      key,
      propertyName: task.siteName,
      address: [task.street, [task.zip, task.city].filter(Boolean).join(' ')].filter(Boolean).join(', '),
      tasks: [task],
    })
  }
  return [...groups.values()]
}

export function PropertyGroup({
  group,
  today,
  showDate,
}: {
  group: Group
  today: string
  showDate?: boolean
}) {
  const open = group.tasks.filter((task) => task.status !== 'done').length
  // Tap the name to fold a property away; finished ones start folded.
  const [expanded, setExpanded] = useState(open > 0)

  return (
    <section className={`wk-group ${expanded ? '' : 'folded'}`}>
      <header>
        <button type="button" className="place" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
          <span className="name">
            <span className="fold" aria-hidden="true">
              {expanded ? '▾' : '▸'}
            </span>{' '}
            {group.propertyName}
          </span>
          {group.address && <span className="addr">{group.address}</span>}
        </button>
        <div className="side">
          <span className="n">
            {open > 0 ? t('group.open', { count: open }) : t('group.finished')}
          </span>
          {group.address && (
            <a
              className="nav"
              href={`https://maps.apple.com/?q=${encodeURIComponent(group.address)}`}
              target="_blank"
              rel="noreferrer"
              aria-label={`Navigation zu ${group.propertyName}`}
            >
              ➜ {t('group.route')}
            </a>
          )}
        </div>
      </header>
      {expanded && group.tasks.map((task) => <TaskCard key={task.id} task={task} today={today} showDate={showDate} compact />)}
    </section>
  )
}
